import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import {CartItemV1} from 'app/modules/Cart';
import {ToastNotification} from 'app/modules/Notification';
import {api} from 'app/modules/Remote';
import {ApiBulkOrderVerificationResponse} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiProject} from 'app/modules/Remote/Project';
import {invalidateImageryBudgets} from 'app/providers/ImageryContractProvider';
import {PER_FEATURE_BULK_ORDER_SOURCE_IDS} from 'app/utils/layers';

import {orderBulkImagery} from './orderImageryUtils';

/** The modal's cart payload before it has been given a cart slot. */
export type CartItemDraft = Omit<CartItemV1, 'id' | 'addedAt'>;

export function useVerifyOrderImageryBulk({
  featureCollection,
  project,
  features,
  sensingTime,
  sourceId,
  sensingTimeMode,
  enabled,
}: {
  featureCollection: ApiFeatureCollection;
  project: ApiProject;
  features: number[];
  sensingTime: string | null;
  sourceId: string;
  sensingTimeMode: 'all' | 'latest' | 'explicit';
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [
      'verifyOrderImageryBulk',
      featureCollection.id,
      project.id,
      features,
      sensingTime,
      sourceId,
      sensingTimeMode,
    ],
    enabled,
    queryFn: () => {
      return api.featureCollections
        .features(featureCollection.id)
        .verifyOrderImageryBulk(project.id, features, sensingTime, sourceId, sensingTimeMode);
    },
    select: (response) => {
      return response.get('data').toJS() as ApiBulkOrderVerificationResponse;
    },
  });
}

export function usePlaceBulkOrder({
  featureCollection,
  project,
  sensingTime,
  sourceId,
  sensingTimeMode,
}: {
  featureCollection: ApiFeatureCollection;
  project: ApiProject;
  sensingTime: string | null;
  sourceId: string;
  sensingTimeMode: 'all' | 'latest' | 'explicit';
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      featureIds,
      expectedCostInCents,
      pushNotification,
    }: {
      featureIds: number[];
      expectedCostInCents: number;
      pushNotification: (p: ToastNotification) => void;
    }) => {
      return orderBulkImagery(
        project.id,
        featureCollection.id,
        featureIds,
        sourceId,
        sensingTime,
        expectedCostInCents,
        pushNotification,
        sensingTimeMode
      );
    },
    // Settled rather than succeeded: a refusal over the cap is as much news about spend as an order.
    // Not returned: the mutation would wait for the refetch, whose spend already counts this order.
    onSettled: () => {
      void invalidateImageryBudgets(queryClient);
    },
  });
}

export function useBulkSensingTime(
  featureCollectionId: number,
  projectId: string,
  featureIds: number[],
  sourceId: string,
  sensingTime?: string
) {
  // Per-feature sources don't use a fixed sensing time — the backend determines
  // the appropriate scenes per feature, so there's nothing to look up here.
  const isPerFeatureSource = PER_FEATURE_BULK_ORDER_SOURCE_IDS.has(sourceId);

  const {data} = useQuery({
    queryKey: ['bulkSensingTime', featureCollectionId, projectId, featureIds[0], sourceId],
    // Per-feature sources never fetch a sensing time — the backend selects scenes per feature
    // using sensingTimeMode ('all' | 'latest'). For package sources, skip fetching if a
    // sensingTime was passed explicitly.
    enabled: !sensingTime && featureIds.length > 0 && !isPerFeatureSource,
    queryFn: () =>
      api.featureCollections
        .features(featureCollectionId)
        .orderableScenes(projectId, featureIds[0], {}, [sourceId]),
    select: (response) => {
      const scenes = response.get('data');
      return scenes.first()?.get('sensingTime') ?? null;
    },
  });

  return useMemo(() => sensingTime ?? data ?? null, [sensingTime, data]);
}
